/** Numbers, money, durations and dates as the page prints them, plus the words for every raw key the API returns. */

export const fmt = (n, digits = 0) =>
  n == null || Number.isNaN(Number(n)) ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: digits });

/** 1,234 → "1.2K", for axis ticks and dense tiles. */
export const compact = (n) =>
  n == null || Number.isNaN(Number(n)) ? "—" : Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(n);

export const pct = (r, digits = 0) => (r == null || Number.isNaN(Number(r)) ? "—" : `${(r * 100).toFixed(digits)}%`);

/** Dollars: $0.00 for nothing, four places only for amounts under a cent. */
export const usd = (n) => (n == null ? "—" : n === 0 ? "$0.00" : `$${Number(n).toFixed(Math.abs(n) < 0.01 ? 4 : 2)}`);

/** Money on an axis or tile: cents under a dollar, compact above. */
export const usdShort = (n) =>
  n == null ? "—" : n === 0 ? "$0" : Math.abs(n) < 1 ? `$${Number(n).toFixed(Math.abs(n) < 0.1 ? 3 : 2)}` : `$${Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(n)}`;

export const ms = (v) => (v == null ? "—" : v >= 60_000 ? `${(v / 60_000).toFixed(1)}m` : v >= 1000 ? `${(v / 1000).toFixed(1)}s` : `${Math.round(v)}ms`);

export const when = (d) => (d ? new Date(d).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

/** "5 Oct, 15:28" — the year only when it is not this one. */
export const whenShort = (d) => {
  if (!d) return "—";
  const t = new Date(d);
  const sameYear = t.getFullYear() === new Date().getFullYear();
  return t.toLocaleString(undefined, { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }), hour: "2-digit", minute: "2-digit" });
};

/** "3h ago", "2d ago" — for "last active" columns. */
export const ago = (d) => {
  if (!d) return "—";
  const s = (Date.now() - new Date(d).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86_400 * 30) return `${Math.floor(s / 86_400)}d ago`;
  return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

export const dayShort = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "—");

/** Turn a raw key into words when it has no label yet ("ask_user" → "Ask user"). */
export const words = (k) => (k ? String(k).replace(/[_-]+/g, " ").replace(/^\w/, (m) => m.toUpperCase()) : "—");

export const signed = (v) => (Number(v) > 0 ? `+${v}` : Number(v) < 0 ? `−${String(v).replace("-", "")}` : "±0");

/**
 * The one scale every figure on the page is judged on: good, watch, bad, or
 * none (no data yet). Thresholds live beside each figure.
 */
export function statusOf(value, { good, bad, higherIsBetter = true }) {
  if (value == null || Number.isNaN(Number(value))) return "none";
  const v = Number(value);
  if (higherIsBetter) return v >= good ? "good" : v < bad ? "bad" : "watch";
  return v <= good ? "good" : v > bad ? "bad" : "watch";
}

export const FRICTION_LABELS = {
  repeated_card: "Asked the same question again",
  unclear_reply: "Could not understand a reply",
  zero_results: "A search found nothing",
  change_requested: "User changed what the agent was doing",
  error_retry: "Errored and needed a retry",
  credits_blocked: "Blocked: not enough credits",
};

/** What a conversation can create in HP (`EntityKind` in hp-mcp-server). */
export const ENTITY_LABELS = {
  segment: { label: "Segments", one: "Segment" },
  source: { label: "Sources", one: "Source" },
  workflow: { label: "Workflows", one: "Workflow" },
  workflow_task: { label: "Enrichment tasks", one: "Enrichment task" },
  job: { label: "Jobs (imports, enrichments)", one: "Job" },
  post: { label: "Posts", one: "Post" },
  resultset: { label: "Search results", one: "Search result" },
  file: { label: "Files", one: "File" },
};

/** Each problem code (`ProblemCode` in hp-mcp-server): what it means and what usually fixes it. */
export const PROBLEM_LABELS = {
  intake_refused: { label: "Request refused before it started", help: "A file or message the intake gate turned away. No run started, so these never appear in Conversations." },
  guard_blocked: { label: "Blocked as off-topic or manipulation", help: "The input guard stopped typed text. A rise can mean real GTM requests being turned away." },
  confirm_unchecked: { label: "User unchecked a filter we offered", help: "A reading of the request the user rejected on the confirm card. Source 'dna' means the saved ICP put it there." },
  resolver_miss: { label: "A phrase matched no value", help: "Industry, location or title words nothing could place — asked about, kept as a keyword, or not applied. The phrase is in the details." },
  tool_budget_spent: { label: "An agent used up a tool's budget", help: "The tool was withdrawn mid-turn. Often a loop, or a lookup that keeps missing." },
  partial_write: { label: "Some records failed to save", help: "Counted per record. 'All' means nothing in that save landed." },
  history_lost: { label: "Conversation memory not saved or read", help: "An agent answered without something it should have known. Usually a database problem." },
  segment_mapping_empty: { label: "Segment column mapping failed", help: "Enrichment outputs have no column to land in." },
  invalid_payload: { label: "Our search payload was rejected", help: "Our own mapper produced filters the vendor's schema refuses — a bug, never the user's input." },
  job_result_lost: { label: "A job's result can never reach the user", help: "An empty webhook, or queued work with no auth to start it." },
  step_stalled: { label: "A step ran and moved nothing", help: "A step that made no progress, or a resume with nothing to resume — a bug." },
  save_not_started: { label: "A save parked before writing anything", help: "The tool that failed first (create_segment, resolve_segment, …), or 'none' when the import agent stopped on its own." },
};

export const RUN_STATUS = {
  success: { label: "Finished", tone: "good" },
  suspended: { label: "Waiting for user", tone: "neutral" },
  paused: { label: "Set aside", tone: "neutral" },
  running: { label: "In progress", tone: "accent" },
  pending: { label: "Starting", tone: "neutral" },
  failed: { label: "Failed", tone: "bad" },
  canceled: { label: "Stopped by user", tone: "warn" },
};

/** What each agent does, shown beneath its real name. */
export const AGENT_LABELS = {
  "orchestrator-loop": "Plans the work and runs the capabilities",
  orchestrator: "Reads typed replies to the agent's questions",
  discovery: "Turns a request into search filters",
  "import-discovery": "Saves records into segments and sets up enrichment",
  enrichment: "Fills in missing data on records",
  "segment-chat": "Answers questions about a segment",
  skills: "Runs slash commands",
  "social-listening": "Tracks social posts",
  "social-publishing": "Drafts and publishes social posts",
};

export const OUTCOMES = [
  { key: "success", label: "Did what they asked", help: "The user got what they asked for in that turn.", color: "var(--aa-good)" },
  { key: "partial", label: "Did part of it", help: "Some of the request was done, or something was dropped.", color: "var(--aa-warn)" },
  { key: "failure", label: "Did not do it", help: "Wrong thing, an error, or going in circles.", color: "var(--aa-bad)" },
  { key: "in_progress", label: "Waiting on the user", help: "The agent asked a question it needs answered. Not a failure.", color: "var(--aa-neutral)" },
  { key: "not_a_task", label: "Not a task", help: "A greeting or an off-topic message.", color: "var(--aa-faint)" },
];

export const FAILURE_LABELS = {
  misread_request: "Misread the request",
  unsearchable_criterion: "Criterion with no filter",
  no_or_wrong_results: "No or wrong results",
  vendor_or_api_failure: "Vendor / API failure",
  credits_blocked: "Blocked on credits",
  too_many_questions: "Too many questions / loop",
  missing_capability: "Capability we lack",
  out_of_scope: "Out of scope",
  model_error: "Model error",
};

export const TASK_LABELS = {
  find_companies: "Find companies",
  find_contacts: "Find contacts",
  find_more_contacts: "Find more contacts",
  enrich: "Enrich",
  analyse_segment: "Analyse a segment",
  create_records: "Create records",
  question: "Question",
  other: "Other",
};

export const CHANNEL_WORDS = { "v2-ui": "HP app", web: "Web", mcp: "MCP (external AI tools)" };
